import { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";

import {
    AnalyticsAttributesProvider,
    Column,
    Expand,
    Markdown,
    Row,
    useAnalytics,
    useAnalyticsAttributes
} from "../../components";
import { UiAccordionModel } from "../../models";
import {
    BackgroundFieldValue,
    cssColorsFromBackgroundField
} from "../../styles";
import { MapModelToProps } from "../../utils";

import {
    AccordionButton,
    GridItem,
    GridItemContent,
    List,
    ListItem,
    ListItemContent
} from "./UiAccordion.styles";

export type UiAccordionProps = MapModelToProps<UiAccordionModel>;

const UiAccordion = ({
    model: {
        type,
        content: { title, items, appearance, background }
    }
}: UiAccordionProps) => {
    const parentTitle = title;
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const [trackedKeys, setTrackedKeys] = useState<string[]>([]);
    const navigate = useNavigate();
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();

    useEffect(() => {
        setOpenKeys(
            window.location.hash
                .slice(1)
                .split(",")
                .filter((key) => key)
        );
    }, []);

    const toggleOpenKeys = (
        id: string,
        title: string,
        entryTitle: string | undefined,
        parentTitle: string
    ) => {
        const newOpenKeys = [...openKeys.filter((key) => id !== key)];
        if (!openKeys.includes(id)) {
            newOpenKeys.push(id);
            if (!trackedKeys.includes(id)) {
                sendTrackEvent("expandedAccordionItem", {
                    ...analyticsAttributes,
                    event: "expanded",
                    eventComponent: "accordion",
                    eventContainer: type,
                    uuid: id,
                    label: title,
                    labelEnglish: entryTitle,
                    parentLabel: parentTitle
                });
                setTrackedKeys([...trackedKeys, id]);
            }
        }
        navigate(`#${newOpenKeys.join()}`);
        setOpenKeys(newOpenKeys);
    };

    const renderMarkdown = (
        content: string,
        itemTitle: string,
        itemEntryTitle: string | undefined,
        itemParentTitle: string
    ) => (
        <AnalyticsAttributesProvider
            value={{
                eventColumn: itemTitle,
                eventContainer: type,
                labelEnglish: itemEntryTitle,
                parentLabel: itemParentTitle
            }}
        >
            <Markdown>{content}</Markdown>
        </AnalyticsAttributesProvider>
    );

    return appearance === "Grid" ? (
        <Row>
            {items
                ?.filter(({ type }) => type === "uiAccordionItem")
                .map(
                    ({
                        id,
                        content: { entryTitle, title, ariaLabel, content }
                    }) => (
                        <Column
                            key={id}
                            xs={{ align: "left" }}
                            md={{ span: "6" }}
                            lg={{ span: "4" }}
                        >
                            <GridItem
                                data-open={openKeys.includes(id)}
                                css={cssColorsFromBackgroundField(
                                    background as BackgroundFieldValue
                                )}
                            >
                                <h3>
                                    <AccordionButton
                                        aria-expanded={openKeys.includes(id)}
                                        aria-label={ariaLabel}
                                        data-testid="grid-accordion-click"
                                        onClick={() =>
                                            toggleOpenKeys(
                                                id,
                                                title,
                                                entryTitle,
                                                parentTitle
                                            )
                                        }
                                    >
                                        <span aria-hidden="true">
                                            {openKeys.includes(id) ? "-" : "+"}
                                        </span>
                                        {title}
                                    </AccordionButton>
                                </h3>
                                <Expand show={openKeys.includes(id)}>
                                    <GridItemContent>
                                        {renderMarkdown(
                                            content,
                                            title,
                                            entryTitle,
                                            parentTitle
                                        )}
                                    </GridItemContent>
                                </Expand>
                            </GridItem>
                        </Column>
                    )
                )}
        </Row>
    ) : (
        <List>
            {items
                ?.filter(({ type }) => type === "uiAccordionItem")
                .map(
                    (
                        {
                            id,
                            content: { entryTitle, title, ariaLabel, content }
                        },
                        index
                    ) => (
                        <ListItem key={index} data-open={openKeys.includes(id)}>
                            <h3>
                                <AccordionButton
                                    aria-expanded={openKeys.includes(id)}
                                    aria-label={ariaLabel}
                                    data-testid="list-accordion-click"
                                    onClick={() =>
                                        toggleOpenKeys(
                                            id,
                                            title,
                                            entryTitle,
                                            parentTitle
                                        )
                                    }
                                >
                                    <span aria-hidden="true">
                                        {openKeys.includes(id) ? "-" : "+"}
                                    </span>
                                    {title}
                                </AccordionButton>
                            </h3>
                            <Expand show={openKeys.includes(id)}>
                                <ListItemContent>
                                    {renderMarkdown(
                                        content,
                                        title,
                                        entryTitle,
                                        parentTitle
                                    )}
                                </ListItemContent>
                            </Expand>
                        </ListItem>
                    )
                )}
        </List>
    );
};

export default UiAccordion;
